/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { CommonUserContextService, ConfigurationHttpRequestInterceptor } from '@gh/common-shared';
import { ID, UserData } from '@gh/common-shared-data';
import { REGION_CODE } from '@gh/config';
import { AuthService, Permission } from '@gh/core-auth';
import { isNotNil } from '@gh/core-util';
import { mapTo$, switchMap$, tap$ } from '@gh/rx/operators';

import { SupplierOfficeRestService, SupplierUserProfileRestService } from '@gh/supplier-shared-data';
import { find, isNil } from 'lodash';
import { Observable } from 'rxjs';

import { OfficeInfo, SupplierUserContext, SupplierUserData } from './supplier-user-context';
import { SupplierUserProfileStore } from './supplier-user-profile-store.service';

@Injectable()
export class SupplierUserContextService extends CommonUserContextService<SupplierUserData> implements SupplierUserContext {
  private _offices: OfficeInfo[] = [];
  private _currentOffice: OfficeInfo;

  set offices(offices: OfficeInfo[]) {
    throw new Error('SupplierUserContextService: list of offices cannot be changed');
  }

  get offices(): OfficeInfo[] {
    this.checkUserInitialized();
    return this._offices;
  }

  set currentOffice(office: OfficeInfo) {
    throw new Error(`SupplierUserContextService: to change office use setCurrentOffice method`);
  }

  get currentOffice(): OfficeInfo {
    this.checkUserInitialized();
    return this._currentOffice;
  }

  constructor(authService: AuthService,
              private profileRest: SupplierUserProfileRestService,
              private officeRest: SupplierOfficeRestService,
              private store: SupplierUserProfileStore,
              private configurationHttpRequestInterceptor: ConfigurationHttpRequestInterceptor) {
    super(authService, store);
  }

  init(params: Hash<any>): Observable<UserData> {
    const { acceptedOfficeId } = params;
    return super.init(params).pipe(
      switchMap$((userData) => acceptedOfficeId
        ? this.profileRest.acceptInvitation(acceptedOfficeId).pipe(mapTo$(userData))
        : [userData]),
      switchMap$((userData) =>
        this.officeRest.findAll().pipe(
          tap$((offices) => {
            this._offices = offices;
            if (offices.length) {
              this.setCurrentOffice(offices[0].id);
            }
          }),
          mapTo$(userData))));
  }

  setCurrentOffice(id: ID): void {
    if (this._currentOffice && this._currentOffice.id === id) {
      return;
    }

    const nextOffice = find(this._offices, { id });
    if (isNil(nextOffice)) {
      throw new Error(`SupplierUserContextService: id "${id}" does not correspond to any of loaded offices`);
    }

    this._currentOffice = nextOffice;
    this.configurationHttpRequestInterceptor.setHeader('X-OfficeId', nextOffice.id);
  }

  isOfficeExist(id: ID): boolean {
    return isNotNil(this.getOffice(id));
  }

  getOffice(id: ID): Maybe<OfficeInfo> {
    return find(this._offices, { id });
  }

  updateReceiveEmails(receiveEmails: boolean): void {
    this.store.updateReceiveEmails(receiveEmails);
  }

  hasPermission(permission: Permission): boolean {
    throw new Error('SupplierUserContext: permissions are not supported');
  }

  protected updateUserData(user: Maybe<SupplierUserData>): void {
    // nothing to do
  }

  protected setupHttpHeaders(params: Hash<any>): void {
    this.configurationHttpRequestInterceptor.headers = {
      'X-Region': REGION_CODE,
    };
  }
}
